/**
 * Заметки о релизах — «Что нового» в Lens.
 *
 * Каждый релиз лежит отдельным markdown-файлом в `notes/`. В шапке файла
 * (между строками `---`) — версия, дата и заголовок, дальше обычный markdown:
 *
 *   ---
 *   date: 2026-07-30
 *   title: Доступ к пространству целой группе
 *   version: 2026.07.30
 *   ---
 *
 *   - что изменилось
 *
 * Обязательны только `date` и `title`; `version` — метка, если она у релиза
 * есть.
 *
 * Файлы собираются в бандл на сборке, так что публикация релиза — это новый
 * файл в `notes/` плюс обычный деплой. Отдельного хранения в БД нет намеренно:
 * текст релиза меняется вместе с кодом, который он описывает.
 *
 * Идентификатор релиза — имя файла без расширения. По нему считается
 * «прочитано» (см. `getReleasesSeen` в shared/prefs/store.ts), поэтому имя
 * файла после публикации переименовывать нельзя — у всех снова загорится
 * непрочитанное.
 */

export interface ReleaseNote {
  /** Имя файла без расширения, например `2026-07-30-group-access`. */
  id: string;
  /** Метка версии, если она есть. Необязательна: нумерации релизов в проекте нет. */
  version: string | null;
  /** Дата релиза в формате YYYY-MM-DD. */
  date: string;
  /** Короткий заголовок — одна строка. */
  title: string;
  /** Тело заметки: markdown без шапки. */
  body: string;
}

const FRONTMATTER_RE = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;

/**
 * Разобрать один файл заметки. Файл без шапки или без обязательных полей
 * пропускаем (возвращаем null), а не роняем страницу: опечатка в одном релизе
 * не должна прятать остальные.
 */
export function parseReleaseNote(id: string, raw: string): ReleaseNote | null {
  const match = FRONTMATTER_RE.exec(raw.replace(/^﻿/, ''));
  if (!match) return null;

  const meta: Record<string, string> = {};
  for (const line of (match[1] ?? '').split(/\r?\n/)) {
    const sep = line.indexOf(':');
    if (sep <= 0) continue;
    meta[line.slice(0, sep).trim()] = line
      .slice(sep + 1)
      .trim()
      .replace(/^['"]|['"]$/g, '');
  }

  const { version, date, title } = meta;
  if (!date || !title) return null;

  return { id, version: version || null, date, title, body: raw.slice(match[0].length).trim() };
}

/** Свежие сверху: по дате, при равной дате — по имени файла. */
export function sortReleases(notes: ReleaseNote[]): ReleaseNote[] {
  return [...notes].sort((a, b) => (b.date === a.date ? b.id.localeCompare(a.id) : b.date.localeCompare(a.date)));
}

function loadReleases(): ReleaseNote[] {
  const files = import.meta.glob('./notes/*.md', {
    query: '?raw',
    import: 'default',
    eager: true,
  }) as Record<string, string>;

  const notes: ReleaseNote[] = [];
  for (const [path, raw] of Object.entries(files)) {
    const id = path.replace(/^.*\//, '').replace(/\.md$/, '');
    const note = parseReleaseNote(id, raw);
    if (note) notes.push(note);
  }
  return sortReleases(notes);
}

/** Все заметки о релизах, свежие сверху. */
export const RELEASES: ReleaseNote[] = loadReleases();

/** Самый свежий релиз (или undefined, если заметок ещё нет). */
export const LATEST_RELEASE: ReleaseNote | undefined = RELEASES[0];
